import BumbleTabs from '@bumble/app/components/BumbleTabs';
import CreateModal from '@bumble/app/components/CreateModal';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Indicator } from '@bumble/app/components/Indicator';
import { Loading } from '@bumble/app/components/Loading';
import MultipleSelections from '@bumble/app/components/MultipleSelections';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import SetContacts from '@bumble/app/components/SetContacts';
import SplitPanes from '@bumble/app/components/SplitPanes';
import { TestReachability } from '@bumble/app/components/TestReachability';
import {
  EndpointAgentEdit,
  EndpointCreate,
  EndpointDescEdit,
  EndpointNameEdit,
  EndpointResize,
  EndpointSources,
  EndpointTabAgents,
  EndpointTabDetails,
  EndpointTabFlows,
  EndpointAdvancedVisibility,
} from '@bumble/app/components/endpoint';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import EditIcon from '@mui/icons-material/Edit';
import ExpandOutlinedIcon from '@mui/icons-material/ExpandOutlined';
import NetworkPingIcon from '@mui/icons-material/NetworkPing';
import PeopleIcon from '@mui/icons-material/People';
import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';


const columns: GridColDef[] = [
  {
    field: 'ep_name',
    headerName: 'EP Name',
    flex: 1,
    minWidth: 300,
    disableColumnMenu: true,
  },
  {
    field: 'admin_state',
    headerName: 'Admin State',
    width: 120,
    disableColumnMenu: true,
  },
  {
    field: 'op_state',
    headerName: 'Op State',
    width: 120,
    disableColumnMenu: true,
    renderCell: (params) => {
      return params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A';
    }
  },
  {
    field: 'fqdn',
    headerName: 'App DNS Name',
    width: 500,
    disableColumnMenu: true,
  },
  {
    field: 'deployment',
    headerName: 'Type',
    width: 100,
    disableColumnMenu: true,
  },
  {
    field: 'app_service_id',
    headerName: 'AppService ID',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'ip_addr',
    headerName: 'EP IP Address',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'ep_id',
    headerName: 'EP Id',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'size',
    headerName: 'Size',
    width: 200,
    disableColumnMenu: true,
  },
  // {
  //  field: 'ep_node_id',
  //  headerName: 'EP Node Id',
  //  width: 200,
  //  disableColumnMenu: true,
  // },
  {
    field: 'created_at',
    headerName: 'EP Created at',
    minWidth: 150,
    headerAlign: 'right',
    align: 'right',
    disableColumnMenu: true,
    valueGetter: (value) => {
      if (value === null) return '';
      return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
];

const columnVisible = {
  ep_name: true,
  ep_id: true,
  ep_node_id: true,
  admin_state: true,
  admin_state_change_at: true,
  op_state: true,
  op_state_change_at: true,
  fqdn: true,
  app_service_id: true,
  ip_addr: true,
  tx_bytes: true,
  rx_bytes: true,
  size: true,
  created_at: true,
};

export const Endpoints = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const handleCreateModalOpen = () => {
    setOpenCreateModal(true);
  };
  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);
  const api = useApi();
  const { isLoading, data, refetch } = useQuery(
    ['endpoints'],
    api.listEpApiV1ListEndpointGet.bind(null, {}),
    { enabled: true },
  );

  const [showReachability, setShowReachability] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [showVisibilityDialog, setShowVisibilityDialog] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [showDescDialog, setShowDescDialog] = useState(false);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [showResizeDialog, setShowResizeDialog] = useState(false);
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showContacts, setShowContacts] = useState(false);

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
    } else {
      setSelectedItemId('');
    }
  }, [rowSelectionModel, selectedRowItem]);

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed Endpoints.');
  };

  const removeEndpoint = useMutation(api.removeEpApiV1RemoveEndpointPost, {
    onSuccess() {
      toast.success('Endpoint removed.');
      handleDataRefetch();
    },
    onError: apiError,
  });

  const resetEndpoint = useMutation(api.resetEndpointApiV1ResetEndpointPost, {
    onSuccess() {
      toast.success('Endpoint reset.');
    },
    onError: apiError,
  });

  const handleEndpointDelete = () => {
    removeEndpoint.mutate({
      ep_id: selectedItemId,
    });
  };

  const handleEndpointLatency = () => {
    setShowReachability(true);
  };

  const handleResetEndpoint = () => {
    resetEndpoint.mutate({
      ep_id: selectedItemId,
    });
  };

  const handleCreateModalClose = () => {
    setOpenCreateModal(false);
    handleDataRefetch();
  };

  const {
    // isLoading: detailLoading,
    data: detailData,
    refetch: detailRefetch,
  } = useQuery(
    ['endpointDetail'],
    api.getEndpointDetailApiV1GetEndpointDetailGet.bind(null, {
      ep_id: selectedRowItem,
    }),
    { enabled: false },
  );

  useEffect(() => {
    if (selectedRowItem) {
      detailRefetch();
    }
  }, [detailRefetch, selectedRowItem]);

  return (
    <PageContent>
      <PageHeader pageName="Endpoints" count={data?.data?.length || 0} />
      {isLoading ? (
        <Loading />
      ) : (
        <SplitPanes
          top={
            <Card elevation={0}>
              <CardContent>
                <DataTableGrid
                  rows={
                    data?.data.map((item) => {
                      return {
                        ...item,
                        id: item.ep_id,
                      };
                    }) || []
                  }
                  columns={columns}
                  handleSelectionChange={handleSelectionChange}
                  rowSelectionModel={rowSelectionModel}
                  itemsSelected={rowSelectionModel.length === 1}
                  newLabel="Create Endpoint"
                  newOnClick={handleCreateModalOpen}
                  columnVisibilityModel={columnVisible}
                  // getRowId={(row: Ep) => row?.ep_id}
                  refreshData={handleDataRefetch}
                  menuItems={[
                    {
                      label: 'Edit Name',
                      icon: <EditIcon color="info" fontSize="small" />,
                      onClick: () => setShowNameDialog(true),
                    },
                    {
                      label: 'Edit Description',
                      icon: <EditIcon color="info" fontSize="small" />,
                      onClick: () => setShowDescDialog(true),
                    },
                    {
                      label: 'Test Reachability',
                      icon: <NetworkPingIcon color="info" fontSize="small" />,
                      onClick: handleEndpointLatency,
                      disabled: detailData?.data?.deployment === 'agent',
                    },
                    {
                      label: 'Show Flows',
                      icon: <DeviceHubIcon color="info" fontSize="small" />,
                      onClick: () => setShowSources(true),
                      disabled: detailData?.data?.deployment === 'agent',
                    },
                    {
                      label: 'Advanced Visibility',
                      icon: <VisibilityIcon color="info" fontSize="small" />,
                      onClick: () => setShowVisibilityDialog(true),
                    },
                    {
                      label: 'Setup Contacts',
                      icon: <PermContactCalendarOutlinedIcon color="info" fontSize="small" />,
                      onClick: () => setShowContacts(true),
                    },
                    {
                      label: 'Edit Agent Details',
                      icon: <PeopleIcon color="info" fontSize="small" />,
                      onClick: () => setShowAgentDialog(true),
                      disabled: detailData?.data?.deployment !== 'agent',
                    },
                    {
                      label: 'Resize Endpoints',
                      icon: <ExpandOutlinedIcon color="info" fontSize="small" />,
                      onClick: () => setShowResizeDialog(true),
                      disabled: detailData?.data?.deployment === 'onprem',
                    },
                    {
                      label: 'Reset',
                      icon: <RestartAltIcon color="warning" fontSize="small" />,
                      onClick: () => setShowConfirmReset(true),
                      hasDivider: true,
                    },
                    {
                      label: 'Delete',
                      icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
                      onClick: () => setShowConfirmDelete(true),
                    },
                  ]}
                />
              </CardContent>
            </Card>
          }
          bottom={
            <Card elevation={0} sx={{ mt: 5 }}>
              <CardContent>
                {rowSelectionModel.length === 1 ? (
                  <BumbleTabs
                    tabs={[
                      {
                        el: <EndpointTabDetails data={detailData?.data} />,
                        label: 'Details',
                        id: 0,
                      },
                      {
                        el: <EndpointTabFlows id={selectedItemId} />,
                        label: 'Netflows',
                        id: 1,
                      },
                      {
                        el: <EndpointTabAgents data={detailData?.data} />,
                        label: 'Agents',
                        id: 2,
                      },
                    ]}
                  />
                ) : (
                  <MultipleSelections count={rowSelectionModel.length} />
                )}
              </CardContent>
            </Card>
          }
        />
      )}
      {openCreateModal && (
        <CreateModal
          open={openCreateModal}
          handleClose={handleCreateModalClose}
          title="Create Endpoint"
          info="https://www.bumblebeenet.com/post/how-to-create-a-bumblebee-networks-endpoint"
        >
          <EndpointCreate handleClose={handleCreateModalClose} />
        </CreateModal>
      )}
      {showReachability && selectedItemId && detailData && (
        <DialogStandard
          open={showReachability}
          handleClose={() => setShowReachability(false)}
          title="Test Reachability"
        >
          <TestReachability id={selectedItemId} type="endpoint" />
        </DialogStandard>
      )}
      {showSources && selectedItemId && (
        <DialogStandard
          open={showSources}
          handleClose={() => setShowSources(false)}
          title="Source IPs"
          size="md"
        >
          <EndpointSources epId={selectedRowItem} />
        </DialogStandard>
      )}
      {showNameDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showNameDialog}
          handleClose={() => setShowNameDialog(false)}
          title="Edit Name"
        >
          <EndpointNameEdit
            onSubmit={() => {
              setShowNameDialog(false);
              refetch();
              detailRefetch();
            }}
            endpointNode={selectedItemId}
            endpointName={detailData?.data?.ep_name}
          />
        </DialogStandard>
      )}
      {showDescDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showDescDialog}
          handleClose={() => setShowDescDialog(false)}
          title="Edit Description"
        >
          <EndpointDescEdit
            onSubmit={() => {
              setShowDescDialog(false);
              refetch();
              detailRefetch();
            }}
            endpointNode={selectedItemId}
            endpointDesc={detailData?.data?.description || ''}
          />
        </DialogStandard>
      )}
      {showAgentDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showAgentDialog}
          handleClose={() => setShowAgentDialog(false)}
          title="Edit Agent Details"
        >
          <EndpointAgentEdit
            onSubmit={() => {
              setShowAgentDialog(false);
              refetch();
              detailRefetch();
            }}
            endpointNode={selectedItemId}
            userAgentDnsServers={detailData?.data?.user_agent_dns_servers}
            userAgentCidrs={detailData?.data?.user_agent_cidrs}
          />
        </DialogStandard>
      )}
      {showResizeDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showResizeDialog}
          handleClose={() => setShowResizeDialog(false)}
          title="Resize Endpoints"
        >
          <EndpointResize
            endpointNode={selectedItemId}
            onSubmit={() => {
              setShowResizeDialog(false);
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showVisibilityDialog && selectedItemId && detailData && (
        <DialogStandard
        open={showVisibilityDialog}
        handleClose={() => setShowVisibilityDialog(false)}
        title="Advanced Visibility"
      >
        <EndpointAdvancedVisibility
          endpointNode={selectedItemId}
          proxying_enabled={detailData?.data?.proxying_enabled}
          onSubmit={() => {
            setShowVisibilityDialog(false);
            refetch();
            detailRefetch();
          }}
        />
      </DialogStandard>
      )}
      {showContacts && selectedItemId && detailData && (
        <DialogStandard
          open={showContacts}
          handleClose={() => setShowContacts(false)}
          title="Setup Contacts"
        >
          <SetContacts
            type='endpoint'
            id={selectedItemId || ''}
            primaryContact={detailData?.data?.primary_contact_id || ''}
            secondaryContact={detailData?.data?.secondary_contact_id || ''}
            onSubmit={() => {
              setShowContacts(false);
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showConfirmReset && (
        <DialogConfirm
          open={showConfirmReset}
          handleClose={() => setShowConfirmReset(false)}
          handleConfirm={handleResetEndpoint}
          title="Reset Endpoint?"
          description="Are you certain you wish to proceed with resetting this endpoint? Please note that this action is irreversible and will result in the permanent reset of the endpoint. Proceeding with this action will reset all associated settings and configurations to their default state."
        />
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleEndpointDelete}
          title="Delete Endpoint?"
          description={`Are you certain you wish to proceed with deleting ${selectedItemId}? Please note that this action is irreversible and will result in the permanent removal of the endpoint.`}
          confirmationPhrase={selectedItemId}
        />
      )}
    </PageContent>
  );
};
